import { useIntl } from 'react-intl';
import { useAuth } from '../../../../../modules/auth';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Select from 'react-select';
import { schema } from './Schema';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import set from 'lodash/set';
import { DataSiftField } from '../../../mappings';
import CreatableSelect from 'react-select/creatable';
import { ServiceSelector, UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { isoCountryMap } from '../../../../../modules/helpers/isoCountryMap';

interface Props {
  type: DataSiftField;
  show: boolean;
  onClose: () => void;
  onUpdate: UsePageDataManagerResult['updateField'];
  serviceSelector: ServiceSelector;
}

const AddWebContent = ({ type, show, onClose, onUpdate, serviceSelector }: Props) => {
  const intl = useIntl();
  const { auth, refresh } = useAuth();
  const { setToast } = useToast();
  const noPolicyTitleValue = 'unknown';

  const [errorMessage, setErrorMessage] = useState('');

  const { companyNameSchema, phoneSchema, emailSchema, addressSchema, policySchema } = schema(intl);

  const defaultCompanyName: z.infer<typeof companyNameSchema> = {
    companyName: '',
  };
  const defaultPhone: z.infer<typeof phoneSchema> = {
    phone: '',
  };
  const defaultEmail: z.infer<typeof emailSchema> = {
    email: '',
  };
  const defaultAddress: z.infer<typeof addressSchema> = {
    address: '',
    countryCode: '',
    city: '',
  };
  const defaultPolicy: z.infer<typeof policySchema> = {
    url: '',
    titles: [],
  };
  const defaultValues = {
    ...defaultCompanyName,
    ...defaultPhone,
    ...defaultEmail,
    ...defaultAddress,
    ...defaultPolicy,
  };
  const validateSchema = {
    [DataSiftField.COMPANY_NAME]: companyNameSchema,
    [DataSiftField.PHONE]: phoneSchema,
    [DataSiftField.EMAIL]: emailSchema,
    [DataSiftField.ADDRESS]: addressSchema,
    [DataSiftField.POLICY]: policySchema,
  };
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    register,
    getFieldState,
    resetField,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validateSchema[type]),
    defaultValues,
  });

  const isoCountryOptions = Object.entries(isoCountryMap).map(([code, label]) => ({
    value: code,
    label: label,
    __isNew__: false,
  }));

  const handleClose = () => {
    reset();
    setErrorMessage('');
    onClose();
  };

  const onSubmit: SubmitHandler<typeof defaultValues> = async (formData) => {
    setErrorMessage('');

    try {
      onUpdate(serviceSelector, (currentDraft) => {
        const fieldTypeKey = type.toLowerCase();

        if ([DataSiftField.COMPANY_NAME, DataSiftField.PHONE, DataSiftField.EMAIL].includes(type)) {
          const value =
            type === DataSiftField.COMPANY_NAME
              ? formData.companyName
              : type === DataSiftField.PHONE
              ? formData.phone
              : formData.email;
          const isEmptyValue = !value.trim();

          if (!isEmptyValue) {
            if (!currentDraft?.result?.[fieldTypeKey]) {
              currentDraft.result[fieldTypeKey] = [];
            }
            currentDraft.result[fieldTypeKey].push(value);
          }
        } else if (type === DataSiftField.ADDRESS) {
          if (formData.address.trim()) {
            if (!currentDraft?.result?.[fieldTypeKey]) {
              currentDraft.result[fieldTypeKey] = [];
            }
            currentDraft.result[fieldTypeKey].push({
              text: formData.address,
              country_code: formData.countryCode,
              city: formData.city,
            });
          }
        } else if (type === DataSiftField.POLICY) {
          const trimmedUrl = formData.url.trim();
          if (trimmedUrl) {
            const hasExistingUrl = !!Object.keys(currentDraft?.result?.[fieldTypeKey] ?? {}).find(
              (url) => url === trimmedUrl
            );
            if (hasExistingUrl) {
              if (formData.titles.length === 0) {
                return;
              }
              const oldTitles = currentDraft.result[fieldTypeKey][trimmedUrl];
              const titleSet = new Set<string>();
              if (typeof oldTitles === 'string') {
                if (oldTitles !== noPolicyTitleValue) {
                  titleSet.add(oldTitles);
                }
              } else {
                oldTitles.forEach((title) => titleSet.add(title));
              }
              formData.titles.forEach((title) => titleSet.add(title));
              currentDraft.result[fieldTypeKey][trimmedUrl] =
                titleSet.size === 1 ? titleSet.values().next().value : Array.from(titleSet);
            } else {
              set(
                currentDraft,
                `result.${fieldTypeKey}.["${trimmedUrl}"]`,
                formData.titles.length > 1
                  ? formData.titles
                  : formData.titles?.[0] ?? noPolicyTitleValue
              );
            }
          }
        }
      });

      handleClose();
    } catch (err) {
      setToast({
        show: true,
        type: 'Danger',
        header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
        message: 'Fail to add category.',
      });
      setErrorMessage(err?.message || intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }));
    }
  };

  const renderFields = (type: DataSiftField) => {
    const className = 'form-control form-control-sm fw-normal text-dark';
    switch (type) {
      case DataSiftField.COMPANY_NAME:
        return (
          <Controller
            name='companyName'
            control={control}
            render={({ field, fieldState }) => (
              <input
                {...field}
                type='text'
                className={clsx(className, fieldState.invalid && 'border border-danger')}
                placeholder='New Company Name'
              />
            )}
          />
        );
      case DataSiftField.PHONE:
        return (
          <Controller
            name='phone'
            control={control}
            render={({ field, fieldState }) => (
              <input
                {...field}
                type='text'
                className={clsx(className, fieldState.invalid && 'border border-danger')}
                placeholder='+852 1234 5678'
              />
            )}
          />
        );
      case DataSiftField.EMAIL:
        return (
          <Controller
            name='email'
            control={control}
            render={({ field, fieldState }) => (
              <input
                {...field}
                type='text'
                className={clsx(className, fieldState.invalid && 'border border-danger')}
                placeholder='john_doe@gmail.com'
              />
            )}
          />
        );
      case DataSiftField.ADDRESS:
        return (
          <>
            <Controller
              name='address'
              control={control}
              render={({ field, fieldState }) => (
                <input
                  {...field}
                  type='text'
                  className={clsx(className, fieldState.invalid && 'border border-danger')}
                  placeholder='New Address'
                />
              )}
            />
            <div className='input-group input-group-sm'>
              <label className='input-group-text' htmlFor='cityInput'>
                City
              </label>
              <Controller
                name='city'
                control={control}
                render={({ field, fieldState }) => (
                  <input
                    {...field}
                    type='text'
                    className={clsx(className, fieldState.invalid && 'border border-danger')}
                  />
                )}
              />
              <label className='input-group-text' htmlFor='countrySelect'>
                Country
              </label>
              <Controller
                name='countryCode'
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    id='countrySelect'
                    placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: 0,
                        boxShadow: 'none',
                      }),
                    }}
                    classNames={{
                      control: (state) =>
                        clsx(
                          'h-100 border-gray-300 rounded-end',
                          fieldState.invalid
                            ? 'border-danger'
                            : state.isFocused
                            ? 'border-gray-400'
                            : ''
                        ),
                    }}
                    className='w-50'
                    options={isoCountryOptions}
                    value={isoCountryOptions.find((option) => option.value === field.value)}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                    menuPortalTarget={document.body}
                  />
                )}
              />
            </div>
          </>
        );
      case DataSiftField.POLICY:
        return (
          <>
            <Controller
              name='url'
              control={control}
              render={({ field, fieldState }) => (
                <input
                  {...field}
                  type='text'
                  className={clsx(className, fieldState.invalid && 'border border-danger')}
                  placeholder='http://example.com/policy'
                />
              )}
            />
            <Controller
              name='titles'
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <CreatableSelect
                    {...field}
                    isMulti
                    placeholder='Policy Titles'
                    noOptionsMessage={() =>
                      intl.formatMessage({ id: 'CLIENTS.SELECTION_DESCRIPTION' })
                    }
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: 0,
                        boxShadow: 'none',
                      }),
                    }}
                    classNames={{
                      control: (state) =>
                        clsx(
                          'h-100 rounded',
                          fieldState.invalid
                            ? 'border-danger'
                            : state.isFocused
                            ? 'border-gray-400'
                            : 'border-gray-300'
                        ),
                    }}
                    value={field.value.map((value) => {
                      return { label: value, value: value, __isNew__: false };
                    })}
                    onChange={(e) => {
                      field.onChange(
                        e.map((options: { label: string; value: string; __isNew__: boolean }) => {
                          return options.value.trim();
                        })
                      );
                    }}
                    menuPortalTarget={document.body}
                  />
                );
              }}
            />
          </>
        );
    }
  };

  return (
    <form className='vstack gap-1' onSubmit={handleSubmit(onSubmit)} noValidate>
      <div>{renderFields(type)}</div>

      <div className='d-flex gap-3'>
        <button type='submit' className='btn btn-primary btn-sm p-1 px-2'>
          Add
        </button>
        <button type='button' className='btn btn-sm p-0 text-muted' onClick={onClose}>
          Cancel
        </button>
        {errorMessage && (
          <div className='text-danger my-4 border border-danger rounded p-2'>{errorMessage}</div>
        )}
      </div>
    </form>
  );
};

export { AddWebContent };
